import type { LucideIcon } from 'lucide-react';
import {
  Archive,
  Building2,
  CreditCard,
  FileSignature,
  FileText,
  FolderOpen,
  KeyRound,
  Landmark,
  Workflow,
} from 'lucide-react';
import type { IntegrationKey } from './api';

export interface AppLink {
  id: string;
  name: string;
  url: string;
  icon: LucideIcon;
  description: string;
  integrationKey?: IntegrationKey;
  /** Shown in Overview quick-links grid */
  quickLink?: boolean;
  /** Shown in sidebar External apps */
  sidebar?: boolean;
}

const ACCOUNTING_DOMAIN =
  (import.meta.env.VITE_ACCOUNTING_DOMAIN as string | undefined)?.trim() ||
  'accounting.zerotail.org';

function envUrl(key: string, fallback: string): string {
  const value = import.meta.env[key];
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function hostPathUrl(hostEnv: string, path: string, fallbackHost: string): string {
  const host =
    (import.meta.env[hostEnv] as string | undefined)?.trim() || fallbackHost;
  const normalized = host.startsWith('http') ? host : `https://${host.replace(/\/$/, '')}`;
  return `${normalized.replace(/\/$/, '')}${path}`;
}

/** Static app URLs — override via VITE_* at build time; API can supersede at runtime. */
export const APP_LINKS: AppLink[] = [
  {
    id: 'erpnext',
    name: 'ERPNext',
    url: envUrl('VITE_ERPNEXT_URL', 'https://erp.zerotail.org'),
    icon: Landmark,
    description: 'General ledger, P&L, and ERP workflows',
    integrationKey: 'erpnext',
    quickLink: true,
    sidebar: true,
  },
  {
    id: 'twenty',
    name: 'Twenty CRM',
    url: envUrl('VITE_CRM_URL', 'https://crm.zerotail.org'),
    icon: Building2,
    description: 'Companies, contacts, and deals',
    integrationKey: 'twenty',
    quickLink: true,
    sidebar: true,
  },
  {
    id: 'documenso',
    name: 'Documenso',
    url: envUrl('VITE_SIGN_URL', 'https://sign.zerotail.org'),
    icon: FileSignature,
    description: 'Contracts and e-signatures',
    integrationKey: 'documenso',
    quickLink: true,
    sidebar: true,
  },
  {
    id: 'solidinvoice',
    name: 'SolidInvoice',
    url: envUrl(
      'VITE_INVOICES_URL',
      'https://invoices.zerotail.org'
    ),
    icon: FileText,
    description: 'Invoicing and client billing',
    integrationKey: 'solidinvoice',
    quickLink: true,
    sidebar: true,
  },
  {
    id: 'paperless',
    name: 'Paperless',
    url: envUrl(
      'VITE_DOCS_URL',
      hostPathUrl('VITE_ACCOUNTING_DOMAIN', '/docs', ACCOUNTING_DOMAIN)
    ),
    icon: Archive,
    description: 'Document archive and OCR',
    integrationKey: 'paperless',
    quickLink: true,
    sidebar: true,
  },
  {
    id: 'ocis',
    name: 'oCIS Files',
    url: envUrl('VITE_FILES_URL', 'https://files.zerotail.org'),
    icon: FolderOpen,
    description: 'Private file storage',
    integrationKey: 'ocis',
    quickLink: true,
    sidebar: true,
  },
  {
    id: 'n8n',
    name: 'n8n Flows',
    url: envUrl('VITE_FLOWS_URL', (() => {
      const flowsHost = (import.meta.env.VITE_FLOWS_HOST as string | undefined)?.trim();
      if (flowsHost) {
        const base = flowsHost.startsWith('http') ? flowsHost : `https://${flowsHost.replace(/\/$/, '')}`;
        return `${base}/`;
      }
      return hostPathUrl('VITE_ACCOUNTING_DOMAIN', '/flows/', ACCOUNTING_DOMAIN);
    })()),
    icon: Workflow,
    description: 'Automation workflows',
    integrationKey: 'n8n',
    quickLink: true,
    sidebar: true,
  },
  {
    id: 'pay',
    name: 'Pay Checkout',
    url: envUrl('VITE_PAY_URL', 'https://pay.zerotail.org'),
    icon: CreditCard,
    description: 'Hosted payment checkout',
    quickLink: true,
    sidebar: true,
  },
  {
    id: 'authentik',
    name: 'Authentik',
    url: envUrl('VITE_AUTHENTIK_URL', 'https://auth.zerotail.org'),
    icon: KeyRound,
    description: 'Identity and SSO admin',
    sidebar: true,
  },
];

const grafanaUrl = envUrl('VITE_GRAFANA_URL', '');
if (grafanaUrl) {
  APP_LINKS.push({
    id: 'grafana',
    name: 'Grafana',
    url: grafanaUrl,
    icon: Workflow,
    description: 'Metrics and dashboards',
    sidebar: true,
  });
}

const APP_LINK_BY_ID = new Map(APP_LINKS.map((link) => [link.id, link]));
const APP_LINK_BY_INTEGRATION = new Map(
  APP_LINKS.filter((link) => link.integrationKey).map((link) => [
    link.integrationKey as string,
    link,
  ])
);

export function getAppLink(id: string): AppLink | undefined {
  return APP_LINK_BY_ID.get(id);
}

export function getAppLinkByIntegration(integrationKey: string): AppLink | undefined {
  return APP_LINK_BY_INTEGRATION.get(integrationKey);
}

export function getQuickLinkApps(): AppLink[] {
  return APP_LINKS.filter((link) => link.quickLink);
}

export function getSidebarApps(): AppLink[] {
  return APP_LINKS.filter((link) => link.sidebar);
}

export type AppLinksMap = Record<string, string>;

/** Merge API-provided URLs over static defaults (matched by id). */
export function mergeAppLinkUrls(apiLinks: AppLinksMap | undefined): AppLink[] {
  if (!apiLinks || Object.keys(apiLinks).length === 0) return APP_LINKS;
  return APP_LINKS.map((link) => {
    const override = apiLinks[link.id];
    return override ? { ...link, url: override } : link;
  });
}

export const EXTERNAL_LINK_PROPS = {
  target: '_blank' as const,
  rel: 'noopener noreferrer',
};
